import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../App';
import { formatDate as fmtDate, formatDateTime } from '../utils/datetime';

interface Campaign {
 campaignHash: string;
 title: string;
 isActive: boolean;
 advertiserMoPeerHash?: string;
 advertiserBusinessNumber?: string;
 advertiserName: string;
 advertiserEmail?: string;
 advertiserPhone?: string;
 ownerMoPeerHash: string;
 moFamilyHash: string;
 moFamilyName?: string;
 startDatetime: string;
 endDatetime: string;
 budget: number;
 budgetSpent?: number;
 slotCount?: number;
 screenCount?: number;
 videos?: string[];
 createdAt: string;
 updatedAt: string;
}

interface VideoThumbnail {
 fileHash: string;
 thumbnailPath?: string;
}

const Campaign: React.FC = () => {
 const navigate = useNavigate();
 const { user } = useAppStore();
 const [campaigns, setCampaigns] = useState<Campaign[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
 const [videoThumbnails, setVideoThumbnails] = useState<Map<string, VideoThumbnail>>(new Map());

 // 캠페인 목록 로드
 const loadCampaigns = async () => {
 setIsLoading(true);
 try {
 const response = await fetch('/campaign', {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setCampaigns(data.data || []);
 } else {
 setError('캠페인 목록을 불러오는데 실패했습니다.');
 }
 } catch (error) {
 console.error('캠페인 목록 로드 오류:', error);
 setError('캠페인 목록을 불러오는데 실패했습니다.');
 } finally {
 setIsLoading(false);
 }
 };

 useEffect(() => {
 loadCampaigns();
 }, []);

 // 영상 썸네일 로드
 const loadVideoThumbnails = async (videos: string[]) => {
 const thumbnailsMap = new Map<string, VideoThumbnail>();
 
 for (const fileHash of videos) {
 try {
 const response = await fetch(`/mofile/${fileHash}/info`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 thumbnailsMap.set(fileHash, {
 fileHash,
 thumbnailPath: data.data?.thumbnail_path
 });
 }
 } catch (error) {
 console.error(`Failed to load thumbnail for ${fileHash}:`, error);
 }
 }
 
 setVideoThumbnails(prev => new Map([...prev, ...thumbnailsMap]));
 };

 // 캠페인 목록 로드 후 썸네일 로드
 useEffect(() => {
 const allVideos = campaigns.flatMap(c => c.videos || []);
 if (allVideos.length > 0) {
 loadVideoThumbnails(allVideos);
 }
 }, [campaigns]);

 // 캠페인 삭제
 const deleteCampaign = async (campaignHash: string) => {
 if (!confirm('정말로 이 캠페인을 삭제하시겠습니까?')) return;
 
 try {
 const response = await fetch(`/campaign/${campaignHash}`, {
 method: 'DELETE',
 credentials: 'include'
 });
 
 if (response.ok) {
 await loadCampaigns();
 } else {
 alert('캠페인 삭제에 실패했습니다.');
 }
 } catch (error) {
 console.error('캠페인 삭제 오류:', error);
 alert('캠페인 삭제에 실패했습니다.');
 }
 };

 // 날짜 포맷 — 중앙 헬퍼 위임 (year+month+day+hour+min 다 포함이라 formatDateTime 사용)
 const formatDate = (dateString: string) => formatDateTime(dateString, {
 year: 'numeric', month: '2-digit', day: '2-digit',
 hour: '2-digit', minute: '2-digit',
 });

 // 숫자 포맷 (원화)
 const formatCurrency = (amount: number) => {
 return new Intl.NumberFormat('ko-KR', {
 style: 'currency',
 currency: 'KRW'
 }).format(amount);
 };

 // 상태 표시
 const getStatusBadge = (campaign: Campaign) => {
 const now = new Date();
 const start = new Date(campaign.startDatetime);
 const end = new Date(campaign.endDatetime);
 
 // 사용자가 비활성으로 설정한 경우
 if (!campaign.isActive) {
 return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600">비활성</span>;
 }
 
 // 활성 상태이지만 아직 시작하지 않은 경우
 if (now < start) {
 return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-600">예정</span>;
 }
 
 // 활성 상태이지만 종료된 경우
 if (now > end) {
 return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600">종료</span>;
 }
 
 // 활성 상태이고 진행 중인 경우
 return <span className="px-2 py-1 text-xs bg-green-100 text-green-600">진행중</span>;
 };

 return (
 <div>
 <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
 <h1 className="text-2xl md:text-3xl font-bold text-gray-800">광고 캠페인</h1>
 <button
 onClick={() => navigate('/campaign/new')}
 className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
 >
 + 새 캠페인 만들기
 </button>
 </div>
 <p className="text-sm text-gray-500 leading-relaxed mb-6">
 특정 영상이 언제·어디서·얼마나 송출됐는지 추적해 캠페인 성과 리포트를 받을 수 있습니다.
 </p>

 {error && (
 <div className="bg-red-50 text-red-800 p-4 mb-4">
 {error}
 </div>
 )}

 {isLoading ? (
 <div className="text-center py-8">
 <div className="inline-block animate-spin h-8 w-8 rounded-full border-4 border-gray-200 border-t-gray-900"></div>
 <p className="mt-2 text-gray-600">로딩 중...</p>
 </div>
 ) : campaigns.length === 0 ? (
 <div className="bg-white p-12 text-center">
 <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
 </svg>
 <h3 className="text-xl font-medium mb-2">아직 캠페인이 없습니다</h3>
 <p className="text-gray-500 mb-6">첫 번째 광고 캠페인을 만들어보세요</p>
 <button
 onClick={() => navigate('/campaign/new')}
 className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3"
 >
 첫 캠페인 만들기
 </button>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-6">
 {campaigns.map((campaign) => (
 <div key={campaign.campaignHash} className="bg-white border border-gray-200">
 <div className="p-6">
 {/* 헤더 */}
 <div className="flex justify-between items-start mb-4">
 <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
 {campaign.title}
 </h3>
 {getStatusBadge(campaign)}
 </div>
 
 {/* 광고주 정보 */}
 <div className="mb-4">
 <div className="text-sm text-gray-500">광고주</div>
 <div className="text-sm font-medium text-gray-900">{campaign.advertiserName}</div>
 {campaign.advertiserEmail && (
 <div className="text-xs text-gray-500">{campaign.advertiserEmail}</div>
 )}
 </div>
 
 {/* 모패밀리 */}
 <div className="mb-4">
 <div className="text-sm text-gray-500">모패밀리</div>
 <div className="text-sm font-medium text-gray-900">
 {campaign.moFamilyName || campaign.moFamilyHash.substring(0, 12) + '...'}
 </div>
 </div>
 
 {/* 기간 */}
 <div className="mb-4">
 <div className="text-sm text-gray-500">캠페인 기간</div>
 <div className="text-sm text-gray-900">
 {fmtDate(campaign.startDatetime)} ~ {fmtDate(campaign.endDatetime)}
 </div>
 </div>
 
 {/* 예산 및 통계 */}
 <div className="grid grid-cols-2 gap-4 mb-4">
 <div>
 <div className="text-sm text-gray-500">예산</div>
 <div className="text-sm font-medium text-gray-900">
 {formatCurrency(campaign.budget)}
 </div>
 </div>
 <div>
 <div className="text-sm text-gray-500">슬롯</div>
 <div className="text-sm font-medium text-gray-900">
 {campaign.slotCount || 0}개
 </div>
 </div>
 </div>
 
 {/* 영상 썸네일 */}
 {campaign.videos && campaign.videos.length > 0 && (
 <div className="mb-4">
 <div className="text-sm text-gray-500 mb-2">캠페인 영상</div>
 <div className="grid grid-cols-3 gap-2">
 {campaign.videos.slice(0, 3).map((fileHash) => {
 const thumbnail = videoThumbnails.get(fileHash);
 return (
 <div key={fileHash} className="aspect-video bg-gray-100 overflow-hidden">
 {thumbnail?.thumbnailPath ? (
 <img
 src={`/uploads/thumbnails/${thumbnail.thumbnailPath}`}
 alt="영상 썸네일"
 className="w-full h-full object-cover"
 />
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
 </svg>
 </div>
 )}
 </div>
 );
 })}
 {campaign.videos.length > 3 && (
 <div className="col-span-3 text-xs text-gray-500 text-center">
 +{campaign.videos.length - 3}개 더
 </div>
 )}
 </div>
 </div>
 )}
 
 {/* 액션 버튼들 */}
 <div className="flex gap-2 pt-4 border-t">
 <button
 onClick={() => navigate(`/campaign/${campaign.campaignHash}`)}
 className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
 >
 상세보기
 </button>
 <button
 onClick={() => navigate(`/campaign/${campaign.campaignHash}/edit`)}
 className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
 >
 수정
 </button>
 <button
 onClick={() => deleteCampaign(campaign.campaignHash)}
 className="px-3 py-2 text-sm bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
 >
 삭제
 </button>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
};

export default Campaign;